import { useEffect, useState } from "react";
import { UserPlus, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHead, TableSkeleton } from "./ui";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { ChangePasswordCard } from "./ChangePasswordCard";

export default function AdminSettings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: "", password: "", name: "" });

  const load = () => api.get("/admin/settings").then((r) => {
    setData(r.data);
    setForm(r.data.settings);
  });
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        site_name: form.site_name,
        tagline: form.tagline,
        commission_rate: Number(form.commission_rate),
        featured_listing_price: Number(form.featured_listing_price),
      });
      toast.success("Platform settings saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addAdmin = async () => {
    if (!newAdmin.email || !newAdmin.password) {
      toast.error("Email and password are required");
      return;
    }
    try {
      await api.post("/admin/settings/admins", newAdmin);
      toast.success(`${newAdmin.email} is now an admin`);
      setNewAdmin({ email: "", password: "", name: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add admin");
    }
  };

  const removeAdmin = async (email) => {
    if (!window.confirm(`Revoke admin access for ${email}?`)) return;
    try {
      await api.delete(`/admin/settings/admins/${encodeURIComponent(email)}`);
      toast.success(`Admin access revoked for ${email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove admin");
    }
  };

  return (
    <div data-testid="admin-settings">
      <PageHead title="Settings" subtitle="Platform configuration & admin accounts" />
      {!form ? <TableSkeleton rows={4} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-white p-6 shadow-sm border border-black/5" data-testid="settings-platform-card">
            <h2 className="font-heading font-bold text-[#111827] mb-5">Platform Configuration</h2>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-muted-foreground">Site Name</Label>
                <Input value={form.site_name || ""} onChange={(e) => set("site_name", e.target.value)}
                  data-testid="settings-site-name" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-muted-foreground">Tagline</Label>
                <Input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)}
                  data-testid="settings-tagline" className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground">Commission Rate (%)</Label>
                  <Input type="number" value={form.commission_rate ?? ""} onChange={(e) => set("commission_rate", e.target.value)}
                    data-testid="settings-commission-rate" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground">Featured Listing ($/wk)</Label>
                  <Input type="number" value={form.featured_listing_price ?? ""} onChange={(e) => set("featured_listing_price", e.target.value)}
                    data-testid="settings-featured-price" className="mt-1.5" />
                </div>
              </div>
              <Button onClick={saveSettings} disabled={saving} data-testid="settings-save-btn"
                className="bg-[#0D5C75] hover:bg-[#0a4a5f] text-white">
                <Save size={16} className="mr-1.5" /> {saving ? "Saving…" : "Save Settings"}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm border border-black/5" data-testid="settings-admins-card">
            <h2 className="font-heading font-bold text-[#111827] mb-5">Admin Accounts</h2>
            <div className="space-y-2 mb-5">
              {(data?.admins || []).map((a) => (
                <div key={a.email} className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2.5" data-testid={`admin-account-${a.email}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[#111827] truncate">{a.name || "Admin"}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.email}</div>
                  </div>
                  {a.email !== user?.email && (
                    <Button size="sm" variant="ghost" className="text-rose-600 shrink-0"
                      onClick={() => removeAdmin(a.email)} data-testid={`admin-remove-${a.email}`}>
                      <Trash2 size={15} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-black/5 pt-4 space-y-3">
              <div className="text-sm font-semibold text-[#111827]">Grant admin access</div>
              <Input placeholder="Name" value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} data-testid="new-admin-name" />
              <Input placeholder="Email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} data-testid="new-admin-email" />
              <Input placeholder="Password" type="password" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} data-testid="new-admin-password" />
              <Button onClick={addAdmin} variant="outline" data-testid="add-admin-btn" className="w-full">
                <UserPlus size={16} className="mr-1.5" /> Add Admin
              </Button>
            </div>
          </div>
          <ChangePasswordCard />
        </div>
      )}
    </div>
  );
}
